import { useState, useEffect, useCallback } from 'react'
import { OfficeContext, OfficeSelection } from '../types'
import { createOfficeService, OfficeService } from '../services/office.service'
import { initializeOffice } from '../utils/office.utils'

interface UseOfficeState {
  context: OfficeContext | null
  isReady: boolean
  error: Error | null
  service: OfficeService | null
}

export const useOffice = () => {
  const [state, setState] = useState<UseOfficeState>({
    context: null,
    isReady: false,
    error: null,
    service: null,
  })

  useEffect(() => {
    const init = async () => {
      try {
        const context = await initializeOffice()
        const service = createOfficeService(context.host)
        await service.initialize()

        setState({
          context,
          isReady: true,
          error: null,
          service,
        })
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error : new Error('Failed to initialize Office'),
        }))
      }
    }

    init()
  }, [])

  const getSelection = useCallback(async (): Promise<OfficeSelection | null> => {
    if (!state.service) return null
    
    try {
      return await state.service.getSelection()
    } catch (error) {
      console.error('Failed to get selection:', error)
      return null
    }
  }, [state.service])

  const insertText = useCallback(async (text: string): Promise<boolean> => {
    if (!state.service) return false
    
    try {
      await state.service.insertText(text)
      return true
    } catch (error) {
      console.error('Failed to insert text:', error)
      return false
    }
  }, [state.service])

  const insertHtml = useCallback(async (html: string): Promise<boolean> => {
    if (!state.service) return false
    
    try {
      await state.service.insertHtml(html)
      return true
    } catch (error) {
      console.error('Failed to insert HTML:', error)
      return false
    }
  }, [state.service])

  const showDialog = useCallback(async (url: string, options?: Office.DialogOptions) => {
    if (!state.isReady) return

    return new Promise<Office.Dialog>((resolve, reject) => {
      Office.context.ui.displayDialogAsync(
        url,
        options || { width: 50, height: 50 },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value)
          } else {
            reject(result.error)
          }
        }
      )
    })
  }, [state.isReady])

  return {
    ...state,
    getSelection,
    insertText,
    insertHtml,
    showDialog,
  }
}